import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const getBaseRouteName = routeName => routeName?.replace(/^mobile_/, '');

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions, routeAccountId) => {
  const accountId = routeAccountId || to.params.accountId;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user, accountId, account) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  const userPermissions = getUserPermissions(user, accountId);

  const settingsPath = `/space/${accountId}/settings`;
  const path = to.path || '';
  const isSettingsRoute =
    path === settingsPath || path.startsWith(`${settingsPath}/`);
  const isIntegrationRoute = path.startsWith(`${settingsPath}/integrations`);

  if (
    (account.restrictions?.restrict_workspace_settings &&
      isSettingsRoute &&
      !isIntegrationRoute) ||
    (account.restrictions?.restrict_integration_settings && isIntegrationRoute)
  ) {
    return defaultRedirectPage(to, userPermissions, accountId);
  }

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible
    ? null
    : defaultRedirectPage(to, userPermissions, accountId);
};

export const validateLoggedInRoutes = (to, user) => {
  const organizationId = Number(to.params.organizationId);
  const organizationAccount = user.accounts?.find(
    account => account.organization_id === organizationId
  );
  const accountId = Number(
    to.params.accountId ||
      organizationAccount?.id ||
      (to.path.startsWith('/user/') ? user.account_id : null)
  );
  const currentAccount = getCurrentAccount(user, accountId);
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return 'login';
  }

  const isCurrentAccountActive =
    currentAccount.status === 'active' &&
    currentAccount.organization_status !== 'suspended';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, accountId, currentAccount);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversations_index',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(getBaseRouteName(routeName));
};

export const getConversationDashboardRoute = routeName => {
  switch (getBaseRouteName(routeName)) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(getBaseRouteName(routeName));
};

export const isNotificationRoute = routeName =>
  getBaseRouteName(routeName) === 'notifications_index';
